import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { generateCacheKey, withManualCache } from '@/utlility/cache-service';

const API_URL = process.env.NEXT_PUBLIC_API_URL;

// Fetch all categories
export const fetchCategories = createAsyncThunk(
  'categories/fetchCategories',
  async ({ organizationId, categoryId, subCategoryId }, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/categories`;
      const payload = {
        organization_key: organizationId,
        category_id: categoryId,  
        sub_category_id: subCategoryId, 
      };
      const cacheKey = generateCacheKey(url, 'POST', payload);
      return await withManualCache(cacheKey, async () => {
        const response = await axios.post(url, payload);
        return response.data;
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);
export const fetchSimilarCategories = createAsyncThunk(
  'categories/fetchSimilarCategories',
  async ({ organizationId, categoryId, subCategoryId }, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/similar-categories`;
      const payload = {
        organization_key: organizationId,
        category_id: categoryId,
        sub_category_id: subCategoryId,
      };
      const cacheKey = generateCacheKey(url, 'POST', payload);
      return await withManualCache(cacheKey, async () => {
        const response = await axios.post(url, payload);
        return response.data;
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Fetch single category by ID
export const fetchCategoryDetails = createAsyncThunk(
  'categories/fetchCategoryDetails',
  async (categoryId, { rejectWithValue }) => {
    try {
      const url = `${API_URL}/api/category/${categoryId}`;
      const cacheKey = generateCacheKey(url, 'GET');
      return await withManualCache(cacheKey, async () => {
        const response = await axios.get(url);
        return response.data;
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      return rejectWithValue({ message: errorMessage });
    }
  }
);

const categoriesSlice = createSlice({
  name: 'categories',
  initialState: {
    categories: [],
    similarCategory: [],
    categoryDetails: null,
    status: 'idle',
    detailsStatus: 'idle',
    error: null,
    detailsError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handling all categories fetching
      .addCase(fetchCategories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.categories = action.payload; // Use action.payload, which is now only the data
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Handling single category fetching
      .addCase(fetchCategoryDetails.pending, (state) => {
        state.detailsStatus = 'loading';
      })
      .addCase(fetchCategoryDetails.fulfilled, (state, action) => {
        state.detailsStatus = 'succeeded';
        state.categoryDetails = action.payload; // Use action.payload, which is now only the data
      })
      .addCase(fetchCategoryDetails.rejected, (state, action) => {
        state.detailsStatus = 'failed';
        state.detailsError = action.payload;
      })

      .addCase(fetchSimilarCategories.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSimilarCategories.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.similarCategory = action.payload; // Use action.payload, which is now only the data
      })
      .addCase(fetchSimilarCategories.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export default categoriesSlice.reducer;
